import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { featureFlagsApi, FeatureFlag, CreateFeatureFlagRequest, UpdateFeatureFlagRequest } from '../../lib/featureFlagsApi';

interface FeatureFlagsState {
  flags: FeatureFlag[];
  enabledKeys: string[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: FeatureFlagsState = {
  flags: [],
  enabledKeys: [],
  loading: false,
  error: null,
  initialized: false,
};

// Async thunks
export const fetchFeatureFlags = createAsyncThunk(
  'featureFlags/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await featureFlagsApi.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch feature flags');
    }
  }
);

export const fetchEnabledFeatures = createAsyncThunk(
  'featureFlags/fetchEnabled',
  async (_, { rejectWithValue }) => {
    try {
      const response = await featureFlagsApi.getEnabled();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch enabled features');
    }
  }
);

export const checkFeature = createAsyncThunk(
  'featureFlags/checkFeature',
  async (key: string, { rejectWithValue }) => {
    try {
      const response = await featureFlagsApi.checkFeature(key);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check feature');
    }
  }
);

export const createFeatureFlag = createAsyncThunk(
  'featureFlags/create',
  async (data: CreateFeatureFlagRequest, { rejectWithValue }) => {
    try {
      const response = await featureFlagsApi.create(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create feature flag');
    }
  }
);

export const updateFeatureFlag = createAsyncThunk(
  'featureFlags/update',
  async ({ id, data }: { id: string; data: UpdateFeatureFlagRequest }, { rejectWithValue }) => {
    try {
      const response = await featureFlagsApi.update(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update feature flag');
    }
  }
);

export const toggleFeatureFlag = createAsyncThunk(
  'featureFlags/toggle',
  async ({ id, isEnabled }: { id: string; isEnabled: boolean }, { rejectWithValue }) => {
    try {
      const response = await featureFlagsApi.toggle(id, isEnabled);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle feature flag');
    }
  }
);

export const toggleFeatureFlagByKey = createAsyncThunk(
  'featureFlags/toggleByKey',
  async ({ key, isEnabled }: { key: string; isEnabled: boolean }, { rejectWithValue }) => {
    try {
      const response = await featureFlagsApi.toggleByKey(key, isEnabled);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle feature flag');
    }
  }
);

export const deleteFeatureFlag = createAsyncThunk(
  'featureFlags/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await featureFlagsApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete feature flag');
    }
  }
);

export const initializeDefaultFeatures = createAsyncThunk(
  'featureFlags/initializeDefaults',
  async (_, { rejectWithValue }) => {
    try {
      await featureFlagsApi.initializeDefaults();
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to initialize default features');
    }
  }
);

const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all feature flags
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.loading = false;
        state.flags = action.payload;
        state.enabledKeys = action.payload.filter(flag => flag.isEnabled).map(flag => flag.key);
        state.initialized = true;
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch enabled features
      .addCase(fetchEnabledFeatures.fulfilled, (state, action) => {
        state.enabledKeys = action.payload;
      })

      // Create feature flag
      .addCase(createFeatureFlag.fulfilled, (state, action) => {
        state.flags.push(action.payload);
        if (action.payload.isEnabled) {
          state.enabledKeys.push(action.payload.key);
        }
      })

      // Update feature flag
      .addCase(updateFeatureFlag.fulfilled, (state, action) => {
        const index = state.flags.findIndex(flag => flag.id === action.payload.id);
        if (index !== -1) {
          const oldFlag = state.flags[index];
          state.flags[index] = action.payload;
          
          // Update enabled keys
          if (oldFlag.isEnabled && !action.payload.isEnabled) {
            state.enabledKeys = state.enabledKeys.filter(key => key !== action.payload.key);
          } else if (!oldFlag.isEnabled && action.payload.isEnabled) {
            state.enabledKeys.push(action.payload.key);
          }
        }
      })

      // Toggle feature flag
      .addCase(toggleFeatureFlag.fulfilled, (state, action) => {
        const index = state.flags.findIndex(flag => flag.id === action.payload.id);
        if (index !== -1) {
          state.flags[index] = action.payload;
          
          // Update enabled keys
          if (action.payload.isEnabled) {
            if (!state.enabledKeys.includes(action.payload.key)) {
              state.enabledKeys.push(action.payload.key);
            }
          } else {
            state.enabledKeys = state.enabledKeys.filter(key => key !== action.payload.key);
          }
        }
      })

      // Toggle feature flag by key
      .addCase(toggleFeatureFlagByKey.fulfilled, (state, action) => {
        const index = state.flags.findIndex(flag => flag.key === action.payload.key);
        if (index !== -1) {
          state.flags[index] = action.payload;
          
          // Update enabled keys
          if (action.payload.isEnabled) {
            if (!state.enabledKeys.includes(action.payload.key)) {
              state.enabledKeys.push(action.payload.key);
            }
          } else {
            state.enabledKeys = state.enabledKeys.filter(key => key !== action.payload.key);
          }
        }
      })

      // Delete feature flag
      .addCase(deleteFeatureFlag.fulfilled, (state, action) => {
        const flagToDelete = state.flags.find(flag => flag.id === action.payload);
        state.flags = state.flags.filter(flag => flag.id !== action.payload);
        if (flagToDelete) {
          state.enabledKeys = state.enabledKeys.filter(key => key !== flagToDelete.key);
        }
      })

      // Initialize default features
      .addCase(initializeDefaultFeatures.fulfilled, () => {
        // Will trigger a refetch of flags
      });
  },
});

export const { clearError, setInitialized } = featureFlagsSlice.actions;

// Selectors
export const selectFeatureFlags = (state: { featureFlags: FeatureFlagsState }) => state.featureFlags.flags;
export const selectEnabledFeatures = (state: { featureFlags: FeatureFlagsState }) => state.featureFlags.enabledKeys;
export const selectFeatureFlagsLoading = (state: { featureFlags: FeatureFlagsState }) => state.featureFlags.loading;
export const selectFeatureFlagsError = (state: { featureFlags: FeatureFlagsState }) => state.featureFlags.error;
export const selectFeatureFlagsInitialized = (state: { featureFlags: FeatureFlagsState }) => state.featureFlags.initialized;

// Helper selector to check if a feature is enabled
export const selectIsFeatureEnabled = (key: string) => (state: { featureFlags: FeatureFlagsState }) => {
  return state.featureFlags.enabledKeys.includes(key);
};

export default featureFlagsSlice.reducer;